/**
 * EntitySelector Component
 * 
 * Generic reusable component for selecting entities with:
 * - Search functionality
 * - Pagination
 * - Multiple/single selection
 * - Customizable columns
 * - Loading and error states
 * 
 * @component
 * @example
 * <EntitySelector
 *   entities={agents}
 *   selectedIds={selectedAgentIds}
 *   columns={agentColumns}
 *   onSelectionChange={setSelectedAgentIds}
 *   onSearch={handleSearch}
 * />
 */

import { useMemo, useRef, useEffect } from 'react';
import { 
  FiSearch, 
  FiChevronLeft, 
  FiChevronRight,
  FiInbox,
  FiLoader,
  FiAlertCircle,
} from 'react-icons/fi';
import type { EntitySelectorProps, SelectableEntity } from './EntitySelector.types';
import styles from './EntitySelector.module.css';

/**
 * Generic EntitySelector Component
 */
function EntitySelector<T extends SelectableEntity>({
  entities,
  selectedIds,
  columns,
  onSelectionChange,
  onSearch,
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  isLoading = false,
  error = null,
  searchValue = '',
  title,
  emptyMessage = 'Nenhum item encontrado',
  searchPlaceholder = 'Buscar...',
  showSearch = true,
  showSelectAll = true,
  maxHeight = '400px',
  selectionMode = 'multiple',
}: EntitySelectorProps<T>) {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const wasFocusedRef = useRef(false);

  /**
   * Preserve focus state during re-renders
   */
  useEffect(() => {
    if (wasFocusedRef.current && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [entities, isLoading]);

  /**
   * Check if all entities are selected
   */
  const allSelected = useMemo(() => {
    if (entities.length === 0) return false;
    return entities.every((entity) => selectedIds.includes(entity.id));
  }, [entities, selectedIds]);

  /**
   * Check if some (but not all) entities are selected
   */
  const someSelected = useMemo(() => {
    return selectedIds.length > 0 && !allSelected;
  }, [selectedIds, allSelected]);

  /**
   * Handle search input change
   */
  const handleSearchChange = (value: string) => {
    if (onSearch) {
      onSearch(value);
    }
  };

  /**
   * Track focus state
   */
  const handleFocus = () => {
    wasFocusedRef.current = true;
  };

  /**
   * Track blur state
   */
  const handleBlur = () => {
    wasFocusedRef.current = false;
  };

  /**
   * Handle select all checkbox
   */
  const handleSelectAll = () => {
    if (allSelected) {
      // Deselect all
      const newSelection = selectedIds.filter(
        (id) => !entities.some((entity) => entity.id === id)
      );
      onSelectionChange(newSelection);
    } else {
      // Select all
      const allIds = entities.map((entity) => entity.id);
      const newSelection = [...new Set([...selectedIds, ...allIds])];
      onSelectionChange(newSelection);
    }
  };

  /**
   * Handle individual entity selection
   */
  const handleEntityToggle = (entityId: string) => {
    if (selectionMode === 'single') {
      // Single selection mode
      onSelectionChange([entityId]);
    } else {
      // Multiple selection mode
      if (selectedIds.includes(entityId)) {
        onSelectionChange(selectedIds.filter((id) => id !== entityId));
      } else {
        onSelectionChange([...selectedIds, entityId]);
      }
    }
  };

  /**
   * Handle page change
   */
  const handlePageChange = (newPage: number) => {
    if (onPageChange && newPage >= 1 && newPage <= totalPages) {
      onPageChange(newPage);
    }
  };

  /**
   * Clear all selections
   */
  const handleClearSelection = () => {
    onSelectionChange([]);
  };

  /**
   * Render cell value
   */
  const renderCell = (column: typeof columns[0], entity: T) => {
    const value = entity[column.key as keyof T];
    
    if (column.render) {
      return column.render(value, entity);
    }
    
    return value?.toString() || '-';
  };

  /**
   * Loading State
   */
  if (isLoading) {
    return (
      <div className={styles.entitySelector}>
        {title && <h3 className={styles.title}>{title}</h3>}
        <div className={styles.tableContainer}>
          <div className={styles.loadingState}>
            <FiLoader size={32} className={styles.spinner} />
            <p className={styles.loadingMessage}>Carregando...</p>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Error State
   */
  if (error) {
    return (
      <div className={styles.entitySelector}>
        {title && <h3 className={styles.title}>{title}</h3>}
        <div className={styles.errorState}>
          <FiAlertCircle size={32} className={styles.errorIcon} />
          <p className={styles.errorMessage}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.entitySelector}>
      {/* Header */}
      <div className={styles.header}>
        {title && <h3 className={styles.title}>{title}</h3>}
        
        {/* Search */}
        {showSearch && (
          <div className={styles.searchWrapper}>
            <FiSearch size={16} className={styles.searchIcon} />
            <input
              ref={searchInputRef}
              type="text"
              className={styles.searchInput}
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
          </div>
        )}
      </div>

      {/* Selection Summary */}
      {selectedIds.length > 0 && (
        <div className={styles.selectionSummary}>
          <span className={styles.selectionText}>
            {selectedIds.length} {selectedIds.length === 1 ? 'item selecionado' : 'itens selecionados'}
          </span>
          <button
            className={styles.clearButton}
            onClick={handleClearSelection}
            type="button"
          >
            Limpar seleção
          </button>
        </div>
      )}

      {/* Table */}
      <div className={styles.tableContainer}>
        <div className={styles.tableWrapper} style={{ maxHeight }}>
          <table className={styles.table}>
            <thead>
              <tr>
                {/* Select All Checkbox */}
                {selectionMode === 'multiple' && showSelectAll && (
                  <th className={styles.checkboxHeader}>
                    <input
                      type="checkbox"
                      className={styles.checkbox}
                      checked={allSelected}
                      ref={(input) => {
                        if (input) {
                          input.indeterminate = someSelected;
                        }
                      }}
                      onChange={handleSelectAll}
                      disabled={entities.length === 0}
                    />
                  </th>
                )}
                
                {/* Column Headers */}
                {columns.map((column) => (
                  <th
                    key={String(column.key)}
                    style={{ width: column.width }}
                    className={column.align ? styles[`align${column.align.charAt(0).toUpperCase()}${column.align.slice(1)}`] : ''}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entities.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + (selectionMode === 'multiple' && showSelectAll ? 1 : 0)}>
                    <div className={styles.emptyState}>
                      <FiInbox size={48} className={styles.emptyIcon} />
                      <p className={styles.emptyMessage}>{emptyMessage}</p>
                    </div>
                  </td>
                </tr>
              ) : (
                entities.map((entity) => {
                  const isSelected = selectedIds.includes(entity.id);
                  
                  return (
                    <tr
                      key={entity.id}
                      className={isSelected ? styles.selected : ''}
                    >
                      {/* Selection Checkbox */}
                      {(selectionMode === 'multiple' || selectionMode === 'single') && (
                        <td className={styles.checkboxCell}>
                          <input
                            type={selectionMode === 'single' ? 'radio' : 'checkbox'}
                            className={styles.checkbox}
                            checked={isSelected}
                            onChange={() => handleEntityToggle(entity.id)}
                          />
                        </td>
                      )}
                      
                      {/* Data Columns */}
                      {columns.map((column) => (
                        <td
                          key={String(column.key)}
                          className={column.align ? styles[`align${column.align.charAt(0).toUpperCase()}${column.align.slice(1)}`] : ''}
                        >
                          {renderCell(column, entity)}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {onPageChange && totalPages > 1 && (
          <div className={styles.pagination}>
            <div className={styles.paginationInfo}>
              Página {currentPage} de {totalPages}
            </div>
            <div className={styles.paginationControls}>
              <button
                className={styles.paginationButton}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                type="button"
              >
                <FiChevronLeft size={16} />
                Anterior
              </button>
              <span className={styles.pageNumber}>{currentPage}</span>
              <button
                className={styles.paginationButton}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                type="button"
              >
                Próxima
                <FiChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EntitySelector;
